import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// @ts-ignore - API is a JavaScript file without type definitions
import API from '../context/API.js';

// Types
export interface SalesSummary {
  grossSales: number;
  netSales: number;
  orderCount: number;
  totalRevenue: number;
  itemsSold: number;
  tips: number;
}

export interface Period {
  startDate: string;
  endDate: string;
  weekNumber?: number;
  year?: number;
}

export interface DailyBreakdown {
  [key: string]: number;
}

export interface CustomerStats {
  totalCustomers: number;
  newCustomers: number;
  repeatCustomers: number;
  averageSpend: number;
}

export interface Category {
  name: string;
  revenue: number;
  itemsSold: number;
  orders: number;
}

export interface CategorySales {
  categories: Category[];
}

export interface Item {
  name: string;
  revenue: number;
  quantity: number;
  price: number;
}

export interface ItemSales {
  items: Item[];
}

export interface WeeklyReportData {
  merchantName?: string;
  reportType?: string;
  period?: Period;
  salesSummary: SalesSummary;
  dailyBreakdown?: DailyBreakdown;
  customerStats?: CustomerStats;
  categorySales?: CategorySales;
  itemSales?: ItemSales;
}

export interface WeeklyReportsState {
  reportData: WeeklyReportData | null;
  loading: boolean;
  error: string | null;
  stores: Array<{ id: string; name: string; [key: string]: any }>;
  storesLoading: boolean;
}

const initialState: WeeklyReportsState = {
  reportData: null,
  loading: false,
  error: null,
  stores: [],
  storesLoading: false,
};

// Async thunks
export const fetchWeeklyReport = createAsyncThunk(
  'weeklyReports/fetchWeeklyReport',
  async (
    params: {
      startDate: string;
      merchantId?: string;
      storeId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/analytics/reports/weekly', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          startDate: params.startDate,
          merchantId: params.merchantId,
          store: params.storeId,
        },
      });

      return response.data.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch weekly report'
      );
    }
  }
);

export const fetchStores = createAsyncThunk(
  'weeklyReports/fetchStores',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/stores', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch stores'
      );
    }
  }
);

const weeklyReportsSlice = createSlice({
  name: 'weeklyReports',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearReportData: (state) => {
      state.reportData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch weekly report
      .addCase(fetchWeeklyReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeeklyReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchWeeklyReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stores
      .addCase(fetchStores.pending, (state) => {
        state.storesLoading = true;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storesLoading = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.storesLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearReportData } = weeklyReportsSlice.actions;
export default weeklyReportsSlice.reducer;
